<template>
  <div class="replay-container">
    <el-card class="replay-card" shadow="hover">
      <div slot="header" class="clearfix">
        <span class="header-title">精彩录像</span>
        <el-button type="primary" @click="showUploadDialog = true" class="add-btn">上传录像</el-button>
      </div>
      
      <!-- 搜索筛选 -->
      <div class="search-filter" v-if="replayList.length > 0">
        <el-row :gutter="16">
          <el-col :span="8">
            <el-input
              v-model="searchForm.keyword"
              placeholder="请输入标题或上传者"
              clearable
              @input="handleSearch"
              prefix-icon="el-icon-search"
              size="small"
            ></el-input>
          </el-col>
          <el-col :span="4">
            <el-select
              v-model="searchForm.status"
              placeholder="状态"
              clearable
              @change="handleSearch"
              size="small"
            >
              <el-option label="待审核" value="0"></el-option>
              <el-option label="已通过" value="1"></el-option>
              <el-option label="已拒绝" value="2"></el-option>
            </el-select>
          </el-col>
          <el-col :span="6">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              @change="handleSearch"
              size="small"
            ></el-date-picker>
          </el-col>
          <el-col :span="2">
            <el-button type="success" size="small" @click="resetSearch">重置</el-button>
          </el-col>
        </el-row>
      </div>
      
      <div class="replay-content">
        <!-- 录像列表 -->
        <el-table :data="filteredReplayList" style="width: 100%" border v-if="filteredReplayList.length > 0">
          <el-table-column prop="title" label="标题" min-width="150"></el-table-column>
          <el-table-column prop="description" label="描述" min-width="200">
            <template slot-scope="scope">
              {{ scope.row.description || '暂无描述' }}
            </template>
          </el-table-column>
          <el-table-column prop="fileName" label="文件名" min-width="150"></el-table-column>
          <el-table-column prop="fileSize" label="文件大小" width="100">
            <template slot-scope="scope">
              {{ formatFileSize(scope.row.fileSize) }}
            </template>
          </el-table-column>
          <el-table-column prop="uploadUserNickname" label="上传者" width="100">
            <template slot-scope="scope">
              {{ scope.row.uploadUserNickname || '未知用户' }}
            </template>
          </el-table-column>
          <el-table-column prop="uploadTime" label="上传时间" width="180">
            <template slot-scope="scope">
              {{ formatDate(scope.row.uploadTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="80">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 1 ? 'success' : scope.row.status === 0 ? 'warning' : 'danger'">
                {{ scope.row.status === 1 ? '已通过' : scope.row.status === 0 ? '待审核' : '已拒绝' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="280">
            <template slot-scope="scope">
              <el-button size="small" type="success" @click="analyzeReplay(scope.row.id)" :loading="analyzingId === scope.row.id">分析</el-button>
              <el-button size="small" type="primary" @click="downloadReplay(scope.row.id)">下载</el-button>
              <el-button size="small" type="info" @click="viewDetail(scope.row)">详情</el-button>
              <el-button size="small" type="danger" @click="deleteReplay(scope.row.id)" v-if="isAdmin">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <!-- 空状态 -->
        <div class="empty-state" v-else>
          <el-empty description="暂无精彩录像数据"></el-empty>
        </div>
      </div>
    </el-card>
    
    <!-- 上传录像弹窗 -->
    <el-dialog title="上传精彩录像" :visible.sync="showUploadDialog" width="600px">
      <el-form :model="uploadForm" :rules="uploadRules" ref="uploadFormRef" label-width="100px">
        <el-form-item label="录像标题" prop="title">
          <el-input v-model="uploadForm.title" placeholder="请输入录像标题"></el-input>
        </el-form-item>
        <el-form-item label="录像描述" prop="description">
          <el-input type="textarea" v-model="uploadForm.description" placeholder="请输入录像描述" rows="3"></el-input>
        </el-form-item>
        <el-form-item label="选择文件" prop="file">
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :on-change="handleFileChange"
            :before-upload="beforeUpload"
            accept=".nwg"
            :file-list="fileList"
            :limit="1">
            <el-button size="small" type="primary">点击上传</el-button>
            <div slot="tip" class="el-upload__tip">只能上传.nwg格式文件，且不超过10MB</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="resetUploadForm">取消</el-button>
        <el-button type="primary" @click="submitUploadForm">确定</el-button>
      </div>
    </el-dialog>
    
    <!-- 录像分析结果弹窗 -->
    <el-dialog title="录像数据分析" :visible.sync="showAnalysisDialog" width="1100px" top="3vh">
      <div v-if="analysisResult" class="analysis-result">
        <!-- 基本信息 -->
        <el-descriptions :column="3" border class="analysis-info" size="small">
          <el-descriptions-item label="录像标题">{{ analysisResult.title }}</el-descriptions-item>
          <el-descriptions-item label="文件名">{{ analysisResult.fileName }}</el-descriptions-item>
          <el-descriptions-item label="游戏时长">
            <span class="highlight-value">{{ analysisResult.formattedDuration }}</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
    
    <!-- 录像详情弹窗 -->
    <el-dialog title="录像详情" :visible.sync="showDetailDialog" width="600px">
      <div v-if="currentReplay" class="replay-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="标题">{{ currentReplay.title }}</el-descriptions-item>
          <el-descriptions-item label="文件名">{{ currentReplay.fileName }}</el-descriptions-item>
          <el-descriptions-item label="文件大小">{{ formatFileSize(currentReplay.fileSize) }}</el-descriptions-item>
          <el-descriptions-item label="上传者">{{ currentReplay.uploadUserNickname || '未知用户' }}</el-descriptions-item>
          <el-descriptions-item label="上传时间">{{ formatDate(currentReplay.uploadTime) }}</el-descriptions-item>
          <el-descriptions-item label="状态">{{ currentReplay.status === 1 ? '已通过' : currentReplay.status === 0 ? '待审核' : '已拒绝' }}</el-descriptions-item>
          <el-descriptions-item label="描述" :span="2">{{ currentReplay.description || '暂无描述' }}</el-descriptions-item>
        </el-descriptions>
        <div class="detail-actions" style="margin-top: 20px; text-align: right;">
          <el-button type="primary" @click="downloadReplay(currentReplay.id)">下载录像</el-button>
        </div>
      </div>
    </el-dialog>
    
    <!-- 上传进度弹窗 -->
    <el-dialog title="上传中" :visible.sync="showUploadProgress" width="400px" :close-on-click-modal="false" :close-on-press-escape="false" :show-close="false">
      <div class="upload-progress-container">
        <el-progress :percentage="uploadProgress" :stroke-width="18" :color="customColor"></el-progress>
        <div class="progress-text">
          <span>{{ uploadProgress }}%</span>
          <span class="sub-text">正在上传录像文件，请稍候...</span>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'Replay',
  data() {
    return {
      replayList: [],
      showUploadDialog: false,
      showDetailDialog: false,
      showAnalysisDialog: false,
      showUploadProgress: false,
      currentReplay: null,
      analysisResult: null,
      analyzingId: null,
      uploadProgress: 0,
      fileList: [],
      searchForm: {
        keyword: '',
        status: ''
      },
      dateRange: null,
      uploadForm: {
        title: '',
        description: '',
        file: null
      },
      uploadRules: {
        title: [
          { required: true, message: '请输入录像标题', trigger: 'blur' },
          { min: 2, max: 50, message: '标题长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        file: [
          { required: true, message: '请选择要上传的文件', trigger: 'change' }
        ]
      }
    }
  },
  mounted() {
    this.loadReplayList();
  },
  computed: {
    isAdmin() {
      return this.$store.state.userInfo?.roleId === 1;
    },
    filteredReplayList() {
      let result = [...this.replayList];
      
      // 关键词过滤
      if (this.searchForm.keyword) {
        const keyword = this.searchForm.keyword.toLowerCase();
        result = result.filter(item => 
          (item.title && item.title.toLowerCase().includes(keyword)) || 
          (item.uploadUserNickname && item.uploadUserNickname.toLowerCase().includes(keyword))
        );
      }
      
      // 状态过滤
      if (this.searchForm.status !== '') {
        result = result.filter(item => item.status === parseInt(this.searchForm.status));
      }
      
      // 日期范围过滤
      if (this.dateRange && this.dateRange.length === 2) {
        const start = this.dateRange[0];
        const end = this.dateRange[1];
        result = result.filter(item => {
          const uploadTime = new Date(item.uploadTime);
          return uploadTime >= start && uploadTime <= end;
        });
      }
      
      return result;
    },
    customColor() {
      return {
        '0%': '#67C23A',
        '100%': '#409EFF'
      };
    }
  },
  methods: {
    // 加载录像列表
    loadReplayList() {
      this.$axios.get('/replay/list').then(res => {
        if (res.code === 200) {
          this.replayList = res.data.records;
        } else {
          this.$message.error(res.msg || '获取录像列表失败');
        }
      }).catch(error => {
        console.error('获取录像列表失败:', error);
        this.$message.error('获取录像列表失败');
      });
    },
    
    // 文件变化处理
    handleFileChange(file, fileList) {
      this.fileList = fileList;
      this.uploadForm.file = file.raw;
    },
    
    // 上传前验证
    beforeUpload(file) {
      const isNwg = file.name.toLowerCase().endsWith('.nwg');
      const isLt10M = file.size / 1024 / 1024 < 10;
      
      if (!isNwg) {
        this.$message.error('只能上传.nwg格式文件!');
      }
      if (!isLt10M) {
        this.$message.error('上传文件大小不能超过10MB!');
      }
      
      return isNwg && isLt10M;
    },
    
    // 提交上传表单
    submitUploadForm() {
      this.$refs.uploadFormRef.validate((valid) => {
        if (valid) {
          if (!this.uploadForm.file) {
            this.$message.error('请选择要上传的文件');
            return;
          }
          
          // 构建FormData
          const formData = new FormData();
          formData.append('file', this.uploadForm.file);
          formData.append('title', this.uploadForm.title);
          formData.append('description', this.uploadForm.description);
          formData.append('uploadUserId', this.$store.state.user?.id || 1);
          
          // 显示上传进度弹窗
          this.uploadProgress = 0;
          this.showUploadProgress = true;
          
          // 上传文件
          this.$axios.post('/replay/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              }
            }
          }).then(res => {
            this.showUploadProgress = false;
            if (res.code === 200) {
              this.$message.success('上传成功，等待审核');
              this.resetUploadForm();
              this.loadReplayList();
            } else {
              this.$message.error(res.msg || '上传失败');
            }
          }).catch(error => {
            this.showUploadProgress = false;
            console.error('上传失败:', error);
            this.$message.error('上传失败，请检查网络连接或文件大小');
          });
        }
      });
    },
    
    // 重置上传表单
    resetUploadForm() {
      this.showUploadDialog = false;
      this.$refs.uploadFormRef.resetFields();
      this.$refs.uploadRef.clearFiles();
      this.fileList = [];
      this.uploadForm.file = null;
    },
    
    // 查看详情
    viewDetail(replay) {
      this.currentReplay = replay;
      this.showDetailDialog = true;
    },
    
    // 下载录像
    downloadReplay(id) {
      // 先检查文件是否存在
      this.$axios.get(`/replay/check/${id}`).then(res => {
        if (res.code === 200 && res.data.exists) {
          // 文件存在，直接下载
          window.location.href = `/api/replay/download/${id}`;
        } else {
          this.$message.error(res.data.message || '录像文件不存在，可能已被删除');
        }
      }).catch(error => {
        console.error('检查录像失败:', error);
        this.$message.error('检查录像失败');
      });
    },
    
    // 删除录像
    deleteReplay(id) {
      this.$confirm('确定要删除这个录像吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$axios.delete(`/replay/delete/${id}`).then(res => {
          if (res.code === 200) {
            this.$message.success('删除成功');
            this.loadReplayList();
          } else {
            this.$message.error(res.msg || '删除失败');
          }
        }).catch(error => {
          console.error('删除录像失败:', error);
          this.$message.error('删除失败');
        });
      }).catch(() => {});
    },
    
    // 格式化文件大小
    formatFileSize(size) {
      if (!size) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(size) / Math.log(k));
      return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    
    // 处理搜索
    handleSearch() {
      // 搜索逻辑已经在计算属性中实现
    },
    
    // 重置搜索
    resetSearch() {
      this.searchForm.keyword = '';
      this.searchForm.status = '';
      this.dateRange = null;
    },
    
    // 分析录像
    analyzeReplay(id) {
      this.analyzingId = id;
      this.$axios.get(`/replay/analyze/${id}`).then(res => {
        this.analyzingId = null;
        if (res.code === 200) {
          this.analysisResult = res.data;
          this.showAnalysisDialog = true;
        } else {
          this.$message.error(res.msg || '分析失败');
        }
      }).catch(error => {
        this.analyzingId = null;
        console.error('分析录像失败:', error);
        this.$message.error('分析录像失败');
      });
    },
    
    // 获取APM标签类型
    getApmTagType(apm) {
      if (apm >= 200) return 'danger';
      if (apm >= 150) return 'warning';
      if (apm >= 100) return 'success';
      if (apm >= 50) return 'info';
      return '';
    },
    
    // 获取APM等级
    getApmLevel(apm) {
      if (apm >= 200) return '大师';
      if (apm >= 150) return '高手';
      if (apm >= 100) return '熟练';
      if (apm >= 50) return '普通';
      return '新手';
    },
    
    // 获取APM等级样式类
    getApmLevelClass(apm) {
      if (apm >= 200) return 'level-master';
      if (apm >= 150) return 'level-expert';
      if (apm >= 100) return 'level-skilled';
      if (apm >= 50) return 'level-normal';
      return 'level-novice';
    },
    
    // 获取操作类型百分比
    getActionPercentage(type) {
      if (!this.analysisResult || !this.analysisResult.actionTypes) return 0;
      const total = this.analysisResult.totalActions || 1;
      const value = this.analysisResult.actionTypes[type] || 0;
      return Math.min(100, Math.round((value / total) * 100));
    },
    
    // 获取图表高度
    getChartHeight(apm) {
      if (!this.analysisResult || !this.analysisResult.apmDistribution) return 0;
      const maxApm = Math.max(...this.analysisResult.apmDistribution, 1);
      return Math.max(20, Math.round((apm / maxApm) * 100));
    },
    
    // 是否显示柱状图标签
    shouldShowLabel(index) {
      const len = this.analysisResult.apmDistribution.length;
      if (len <= 10) return true;
      // 太多柱子时，只显示部分标签
      return index === 0 || index === len - 1 || index % 5 === 0;
    },
    
    // 获取分段宽度
    getSegmentWidth(type) {
      const maxApm = Math.max(
        this.analysisResult.earlyGameApm || 0,
        this.analysisResult.midGameApm || 0,
        this.analysisResult.lateGameApm || 0,
        1
      );
      let val = 0;
      if (type === 'early') val = this.analysisResult.earlyGameApm || 0;
      if (type === 'mid') val = this.analysisResult.midGameApm || 0;
      if (type === 'late') val = this.analysisResult.lateGameApm || 0;
      return Math.round((val / maxApm) * 100);
    },
    
    // 获取分段评价
    getSegmentComment(type) {
      const early = this.analysisResult.earlyGameApm || 0;
      const mid = this.analysisResult.midGameApm || 0;
      const late = this.analysisResult.lateGameApm || 0;
      
      if (type === 'early') {
        if (early > mid && early > late) return '开局型';
        if (early < mid && early < late) return '平稳启动';
        return '中规中矩';
      }
      if (type === 'mid') {
        if (mid > early && mid > late) return '发育期高强度';
        return '过渡阶段';
      }
      if (type === 'late') {
        if (late > early && late > mid) return '后期类型';
        if (late < early && late < mid) return '后继乏力';
        return '稳定输出';
      }
      return '';
    },
    
    // 获取玩家类型判断
    getPlayerType() {
      const early = this.analysisResult.earlyGameApm || 0;
      const mid = this.analysisResult.midGameApm || 0;
      const late = this.analysisResult.lateGameApm || 0;
      
      if (late > mid * 1.2 && late > early * 1.2) return '后期爆发型';
      if (early > mid * 1.1 && early > late * 1.1) return '开局压制型';
      if (Math.abs(early - late) < early * 0.2) return '均衡稳定型';
      return '渐进发育型';
    },
    
    // 获取效率样式类
    getEfficiencyClass(efficiency) {
      if (efficiency >= 40) return 'efficiency-high';
      if (efficiency >= 25) return 'efficiency-mid';
      return 'efficiency-low';
    },
    
    // 获取玩家颜色
    getPlayerColor(index) {
      const colors = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#9B59B6'];
      return colors[index % colors.length];
    },
    
    // 表格行样式
    tableRowClassName({row, rowIndex}) {
      if (rowIndex === 0) return 'top-player-row';
      return '';
    },
    
    // 获取最高指标玩家
    getTopPlayer(field) {
      if (!this.analysisResult.players || this.analysisResult.players.length === 0) {
        return { playerName: '-', apm: 0, efficiency: 0 };
      }
      return this.analysisResult.players.reduce((top, p) => {
        return (p[field] || 0) > (top[field] || 0) ? p : top;
      }, this.analysisResult.players[0]);
    },
    
    // 获取编队使用率
    getHotkeyUsageRate() {
      // 优先使用后端计算的值
      if (this.analysisResult.hotkeyUsageRate !== undefined) {
        return this.analysisResult.hotkeyUsageRate;
      }
      if (!this.analysisResult.actionTypes || !this.analysisResult.totalActions) return 0;
      const hotkey = this.analysisResult.actionTypes.hotkeyActions || 0;
      return Math.round((hotkey / this.analysisResult.totalActions) * 1000) / 10;
    },
    
    // 获取每分钟技能操作
    getAbilityPerMinute() {
      // 优先使用后端计算的值
      if (this.analysisResult.abilityPerMinute !== undefined) {
        return this.analysisResult.abilityPerMinute;
      }
      if (!this.analysisResult.actionTypes || !this.analysisResult.gameDuration) return 0;
      const ability = this.analysisResult.actionTypes.abilityActions || 0;
      const minutes = this.analysisResult.gameDuration / 60;
      if (minutes <= 0) return 0;
      return Math.round((ability / minutes) * 10) / 10;
    },
    
    // 获取每分钟选择操作
    getSelectPerMinute() {
      if (this.analysisResult.selectPerMinute !== undefined) {
        return this.analysisResult.selectPerMinute;
      }
      if (!this.analysisResult.actionTypes || !this.analysisResult.gameDuration) return 0;
      const select = this.analysisResult.actionTypes.selectActions || 0;
      const minutes = this.analysisResult.gameDuration / 60;
      if (minutes <= 0) return 0;
      return Math.round((select / minutes) * 10) / 10;
    },
    
    // 获取每分钟编队操作
    getHotkeyPerMinute() {
      if (this.analysisResult.hotkeyPerMinute !== undefined) {
        return this.analysisResult.hotkeyPerMinute;
      }
      if (!this.analysisResult.actionTypes || !this.analysisResult.gameDuration) return 0;
      const hotkey = this.analysisResult.actionTypes.hotkeyActions || 0;
      const minutes = this.analysisResult.gameDuration / 60;
      if (minutes <= 0) return 0;
      return Math.round((hotkey / minutes) * 10) / 10;
    },
    
    // 获取操作类型占比
    getActionPercent(player, type) {
      const total = (player.unitActions || 0) + (player.selectActions || 0) + 
                    (player.hotkeyActions || 0) + (player.abilityActions || 0);
      if (total === 0) return 0;
      let value = 0;
      switch(type) {
        case 'unit': value = player.unitActions || 0; break;
        case 'select': value = player.selectActions || 0; break;
        case 'hotkey': value = player.hotkeyActions || 0; break;
        case 'ability': value = player.abilityActions || 0; break;
      }
      return Math.round((value / total) * 1000) / 10;
    }
  }
}
</script>

<style scoped>
.replay-container {
  padding: 20px 0;
}

.replay-card {
  margin-bottom: 20px;
}

.header-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.add-btn {
  float: right;
}

.search-filter {
  margin: 15px 0;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.replay-content {
  min-height: 400px;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.replay-detail {
  padding: 10px 0;
}

.detail-actions {
  margin-top: 20px;
  text-align: right;
}

.analysis-result {
  padding: 10px 0;
}

.analysis-info {
  margin-bottom: 15px;
}

.highlight-value {
  font-size: 14px;
  font-weight: bold;
  color: #409EFF;
}

/* APM卡片样式 */
.apm-cards {
  margin: 15px 0;
}

.stat-card {
  border-radius: 12px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: transform 0.3s, box-shadow 0.3s;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
}

.stat-card .stat-icon {
  font-size: 36px;
  opacity: 0.9;
}

.stat-card .stat-content {
  flex: 1;
}

.stat-card .stat-value {
  font-size: 28px;
  font-weight: bold;
  line-height: 1.2;
}

.stat-card .stat-label {
  font-size: 14px;
  font-weight: 600;
  margin-top: 2px;
}

.stat-card .stat-desc {
  font-size: 11px;
  opacity: 0.8;
  margin-top: 2px;
}

.apm-card {
  background: linear-gradient(135deg, #67C23A 0%, #85ce61 100%);
  color: white;
}

.eapm-card {
  background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
  color: white;
}

.peak-card {
  background: linear-gradient(135deg, #E6A23C 0%, #ebb563 100%);
  color: white;
}

.efficiency-card {
  background: linear-gradient(135deg, #F56C6C 0%, #f89898 100%);
  color: white;
}

/* 分段统计样式 */
.detail-section {
  margin: 15px 0;
}

.section-card {
  background: #fff;
  border: 1px solid #EBEEF5;
  border-radius: 8px;
  padding: 15px;
}

.section-card h4 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 14px;
  border-left: 3px solid #409EFF;
  padding-left: 8px;
}

/* APM时间分布图 */
.apm-timeline {
  margin-top: 15px;
}

.apm-chart-wrapper {
  padding: 10px 0;
}

.chart-header {
  margin-bottom: 15px;
  padding: 10px;
  background: #f0f9eb;
  border-radius: 4px;
  border: 1px solid #e8f5e8;
}

.chart-info {
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: #606266;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-item strong {
  color: #303133;
  font-weight: 600;
}

.apm-chart {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 4px;
  height: 120px;
  padding: 10px 5px;
  background: linear-gradient(180deg, #f5f7fa 0%, #e8eaed 100%);
  border-radius: 8px;
  overflow-x: auto;
}

.chart-bar {
  min-width: 25px;
  max-width: 40px;
  flex: 1;
  background: linear-gradient(180deg, #409EFF 0%, #67C23A 100%);
  border-radius: 4px 4px 0 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  transition: all 0.3s;
  cursor: pointer;
}

.chart-bar:hover {
  background: linear-gradient(180deg, #66b1ff 0%, #85ce61 100%);
  transform: scaleY(1.05);
}

.chart-bar.peak {
  background: linear-gradient(180deg, #F56C6C 0%, #E6A23C 100%);
}

.bar-label {
  font-size: 9px;
  color: #fff;
  font-weight: bold;
  padding-top: 3px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

.chart-labels {
  display: flex;
  justify-content: flex-start;
  gap: 4px;
  padding: 5px 5px 0;
  font-size: 10px;
  color: #909399;
}

.chart-labels span {
  min-width: 25px;
  max-width: 40px;
  flex: 1;
  text-align: center;
}

.chart-labels span.highlight {
  color: #409EFF;
  font-weight: bold;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 10px 0 0;
  font-size: 12px;
  color: #606266;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.legend-item .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.early-dot { background: #67C23A; }
.mid-dot { background: #E6A23C; }
.late-dot { background: #F56C6C; }
.peak-dot { background: linear-gradient(135deg, #F56C6C, #E6A23C); }

.apm-label {
  margin-left: 5px;
  color: #909399;
}

.player-stats {
  margin: 15px 0;
}

.player-stats h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  border-left: 3px solid #409EFF;
  padding-left: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 操作类型分布图 */
.action-type-distribution {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
}

.dist-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.dist-title {
  font-weight: 600;
  color: #303133;
  font-size: 13px;
}

.dist-legend {
  display: flex;
  gap: 15px;
  font-size: 12px;
  color: #606266;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.player-bars {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.player-bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bar-player-name {
  width: 120px;
  font-size: 12px;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-container {
  flex: 1;
  height: 20px;
  background: #eee;
  border-radius: 4px;
  display: flex;
  overflow: hidden;
}

.bar-segment {
  height: 100%;
  transition: width 0.3s ease;
  cursor: pointer;
}

.bar-segment:hover {
  opacity: 0.8;
}

.bar-segment.unit {
  background: #409EFF;
}

.bar-segment.select {
  background: #67C23A;
}

.bar-segment.hotkey {
  background: #E6A23C;
}

.bar-segment.ability {
  background: #F56C6C;
}

.bar-apm {
  width: 80px;
  font-size: 12px;
  color: #909399;
  text-align: right;
}

.player-name {
  display: flex;
  align-items: center;
  gap: 6px;
}

.number-cell {
  font-family: 'Monaco', monospace;
  color: #606266;
}

.eapm-cell {
  color: #409EFF;
  font-weight: 500;
}

.efficiency-cell {
  font-weight: 500;
}

.efficiency-high { color: #67C23A; }
.efficiency-mid { color: #E6A23C; }
.efficiency-low { color: #909399; }

.level-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.level-master { background: #fef0f0; color: #F56C6C; }
.level-expert { background: #fdf6ec; color: #E6A23C; }
.level-skilled { background: #f0f9eb; color: #67C23A; }
.level-normal { background: #f4f4f5; color: #909399; }
.level-novice { background: #f4f4f5; color: #C0C4CC; }

/* 表格样式 */
.player-stats >>> .el-table .top-player-row {
  background-color: #fdf6ec;
}

.analysis-tips {
  margin-top: 15px;
}

.tips-content {
  font-size: 13px;
  line-height: 1.8;
  color: #606266;
}

.tips-content p {
  margin: 5px 0;
}

.tips-note {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #DCDFE6;
  color: #909399;
}

/* 上传进度样式 */
.upload-progress-container {
  padding: 20px 0;
  text-align: center;
}

.progress-text {
  margin-top: 20px;
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.progress-text .sub-text {
  display: block;
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  margin-top: 5px;
}

/* 分段APM样式 */
.segment-apm {
  padding: 10px 0;
}

.segment-item {
  margin-bottom: 12px;
}

.segment-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 4px;
}

.segment-bar {
  height: 24px;
  background: #f0f2f5;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.segment-fill {
  height: 100%;
  border-radius: 12px;
  transition: width 0.5s ease;
}

.segment-item.early .segment-fill {
  background: linear-gradient(90deg, #67C23A, #85ce61);
}

.segment-item.mid .segment-fill {
  background: linear-gradient(90deg, #E6A23C, #ebb563);
}

.segment-item.late .segment-fill {
  background: linear-gradient(90deg, #F56C6C, #f89898);
}

.segment-value {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  font-weight: bold;
  color: #303133;
}

.segment-comment {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}

.player-type-badge {
  margin-top: 15px;
  text-align: center;
  padding: 8px 16px;
  background: linear-gradient(135deg, #409EFF 0%, #67C23A 100%);
  color: white;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

/* 操作类型样式 */
.action-types {
  padding: 5px 0;
}

.action-type-item {
  margin-bottom: 12px;
}

.action-type-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 13px;
}

.action-type-name {
  color: #606266;
  display: flex;
  align-items: center;
  gap: 5px;
}

.action-type-count {
  color: #909399;
  font-family: 'Monaco', monospace;
}

/* 数据洞察 */
.analysis-insights {
  margin: 15px 0;
}

.analysis-insights h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  border-left: 3px solid #409EFF;
  padding-left: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.insights-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.insight-card {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
}

.insight-title {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.insight-value {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.insight-detail {
  font-size: 11px;
  color: #606266;
}
</style>